import { Emitter } from "@core/events/emitter";
import { isPaidPlan, parsePlanType } from "@models/data/campfirePlanType";
import { Ioc } from "@core/ioc/ioc";
import type { IAuthModel } from "@models/auth/IAuthModel";
import { AUTH_MODEL } from "@models/auth/IAuthModel";
import { type CloudConfig, loadCloudConfig } from "@models/storage/cloudConfig";
import type { CustomerInfo, ICustomerModel } from "./ICustomerModel";

/**
 * `HttpCustomerModel` — production {@link ICustomerModel} backed by
 * the Campfire AppSync admin GraphQL endpoint.
 *
 * Mirrors Unity's `AwsAdminStorageModel.GetCustomerData`:
 *   - POST `cloudConfig.graphqlEndpoint`
 *   - body `{"query": "{ getAccount(accountid: \"<id>\") { name } }"}`
 *   - `Authorization` header is the **raw** Cognito ID token (not
 *     `Bearer …`), matching `SendQuery` in
 *     `Models/StorageModel/AWS/AwsAdminStorageModel.cs`.
 *
 * We only request the `name` field today — the rest of `getAccount`
 * (entitlement, plan type, cloud-storage access, …) lands when the UI
 * needs it.
 */
export class HttpCustomerModel implements ICustomerModel {
  readonly onChanged = new Emitter<CustomerInfo | null>();

  private m_customerInfo: CustomerInfo | null = null;
  private m_inflight: Promise<CustomerInfo | null> | null = null;
  private readonly m_config: CloudConfig;

  constructor(config: CloudConfig = loadCloudConfig()) {
    this.m_config = config;
  }

  get customerInfo(): CustomerInfo | null {
    return this.m_customerInfo;
  }

  async getCustomerAsync(signal?: AbortSignal): Promise<CustomerInfo | null> {
    if (this.m_customerInfo) return this.m_customerInfo;
    if (this.m_inflight) return this.m_inflight;

    this.m_inflight = this.fetchOnce(signal).finally(() => {
      this.m_inflight = null;
    });
    return this.m_inflight;
  }

  reset(): void {
    this.m_customerInfo = null;
    this.m_inflight = null;
    this.onChanged.emit(null);
  }

  // -- Internals --

  private async fetchOnce(signal?: AbortSignal): Promise<CustomerInfo | null> {
    const auth = Ioc.resolve<IAuthModel>(AUTH_MODEL);
    const customerId = auth.currentUser?.customerId;
    if (!customerId) return null;

    const idToken = await auth.getIdToken();
    if (!idToken) return null;

    // `shareAnyone` / `shareDomains` / `safeDomains` are the account's sharing
    // rules (`SharedUsersModel.CanInviteUser`); they are in Unity's own
    // `getAccount` selection (`AwsAdminStorageModel.cs:189-191`).
    const query = `{ getAccount(accountid: "${escapeGraphQlString(customerId)}") { name planType isPaidAccount shareAnyone shareDomains safeDomains } }`;
    let response: Response;
    try {
      response = await fetch(this.m_config.graphqlEndpoint, {
        method: "POST",
        headers: {
          // Unity uses the raw token as the `authorization` header
          // value (not `Bearer …`). AppSync's Cognito-pool
          // authorizer expects this exact shape.
          "authorization": idToken,
          "content-type": "application/json",
        },
        body: JSON.stringify({ query }),
        signal,
      });
    } catch (err) {
      console.warn("[HttpCustomerModel] getAccount fetch failed:", err);
      return null;
    }

    if (!response.ok) {
      console.warn(
        `[HttpCustomerModel] getAccount HTTP ${response.status} ${response.statusText}`,
      );
      return null;
    }

    let payload: GraphQlResponse;
    try {
      payload = (await response.json()) as GraphQlResponse;
    } catch {
      return null;
    }

    const name = payload?.data?.getAccount?.name;
    if (!name) {
      if (payload?.errors?.length) {
        console.warn("[HttpCustomerModel] getAccount errors:", payload.errors);
      }
      return null;
    }

    // `planType` drives `IsProjectPaid` / `CanUsePaidFeatures`. Unity asks
    // `getAccount` for 18 fields (`AwsAdminStorageModel.cs:176-208`); the web
    // asked for one. Adding these two was the cheapest unblock in the whole
    // permissions port — same endpoint, same header, same request.
    const account = payload?.data?.getAccount;
    const planType = parsePlanType(account?.planType);
    const info: CustomerInfo = {
      customerId,
      name,
      planType,
      // Prefer the server's own flag when it sends one; otherwise derive it,
      // remembering that EnterpriseFree counts as PAID.
      isPaidAccount: typeof account?.isPaidAccount === "boolean"
        ? account.isPaidAccount
        : isPaidPlan(planType),
      // Unity reads each only when the JSON token is non-null and otherwise
      // keeps `CustomerData`'s defaults (false / false / empty).
      shareAnyone: account?.shareAnyone === true,
      shareDomains: account?.shareDomains === true,
      safeDomains: typeof account?.safeDomains === "string" && account.safeDomains
        ? account.safeDomains.split(",")
        : [],
    };
    this.m_customerInfo = info;
    this.onChanged.emit(info);
    return info;
  }
}

interface GraphQlResponse {
  data?: {
    getAccount?: {
      name?: string;
      planType?: unknown;
      isPaidAccount?: unknown;
      shareAnyone?: unknown;
      shareDomains?: unknown;
      safeDomains?: unknown;
    };
  };
  errors?: ReadonlyArray<{ message?: string }>;
}

function escapeGraphQlString(value: string): string {
  return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}
